import React from 'react';
import { DivideIcon as LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';

interface FeatureCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  link: string;
  isExternal?: boolean;
}

export function FeatureCard({ icon: Icon, title, description, link, isExternal }: FeatureCardProps) {
  const CardWrapper = isExternal ? 'a' : Link;
  const linkProps = isExternal ? { href: link, target: "_blank", rel: "noopener noreferrer" } : { to: link };

  return (
    <CardWrapper
      {...linkProps}
      className={cn(
        "group relative bg-white p-8 rounded-2xl",
        "border border-gray-100",
        "transition-all duration-300",
        "hover:shadow-xl hover:-translate-y-1",
        "overflow-hidden",
        "block", // Ensure proper display for anchor/link
        "cursor-pointer"
      )}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-teal-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      {/* Content */}
      <div className="relative">
        <div className={cn(
          "w-12 h-12 mb-6",
          "flex items-center justify-center",
          "bg-teal-50 text-teal-600",
          "rounded-xl",
          "group-hover:scale-110 transition-transform duration-300"
        )}>
          <Icon className="h-6 w-6" />
        </div>

        <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
          {title}
        </h3>

        <p className="text-gray-600 leading-relaxed">
          {description}
        </p>

        {/* Arrow indicator */}
        <div className={cn(
          "absolute bottom-0 right-0",
          "opacity-0 group-hover:opacity-100",
          "transform translate-x-full group-hover:translate-x-0",
          "transition-all duration-300"
        )}>
          <div className="p-2 text-teal-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </div>
        </div>
      </div>
    </CardWrapper>
  );
}